import Link from "next/link"

interface BrandFeatureCardProps {
  /** Brand name */
  name: string
  /** List of feature bullet points */
  features: string[]
  /** Link to the brand page */
  href?: string
  /** Brand logo URL (shows initials fallback when absent) */
  logo?: string
  /** Additional classes on the wrapper */
  className?: string
}

/**
 * BrandFeatureCard — Clickable brand card with logo, name, and feature list.
 *
 * Renders as a full-card Link when `href` is provided. Shows the brand logo
 * (or an initial circle fallback), the brand name, and a list of feature
 * bullet points with orange check icons. Used in the "Shop by Brand" sections
 * across category, feature, and specialty hub pages.
 */
export function BrandFeatureCard({
  name,
  features,
  href,
  logo,
  className = "",
}: BrandFeatureCardProps) {
  const content = (
    <>
      <div className="flex items-center gap-3 mb-3">
        {logo ? (
          <img src={logo} alt={`${name} logo`} className="w-8 h-8 object-contain flex-shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-gray-500">{name.charAt(0)}</span>
          </div>
        )}
        <h3 className="font-bold text-gray-900">{name}</h3>
      </div>
      <ul className="space-y-2">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2">
            <svg
              className="w-4 h-4 text-[#ff9951] flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-xs text-gray-600 leading-snug">{feature}</span>
          </li>
        ))}
      </ul>
    </>
  )

  if (href) {
    return (
      <Link
        href={href}
        className={`group bg-white rounded-lg p-5 border border-gray-100 hover:border-[#ff9951]/30 hover:shadow-[0_4px_20px_rgba(0,25,69,0.06)] transition-all block ${className}`}
      >
        {content}
      </Link>
    )
  }

  return (
    <div className={`bg-white rounded-lg p-5 border border-gray-100 ${className}`}>
      {content}
    </div>
  )
}
